import {
  AgentChannelSchema,
  AgentRoleSchema,
  AgentSessionIdSchema,
  AgentSessionStatusSchema,
  ApplicationIdSchema,
  GuardEventSchema,
  HumanTaskSchema,
  LlmEffortSchema,
  MemoryFactSchema,
  TimestampSchema,
  TokenUsageSchema,
  ToolInvocationStatusSchema,
  paginatedSchema,
} from '@paguz/contracts';
import { z } from 'zod';

/**
 * Vistas de consola de agentes (`/v1/admin/agent/*`, `06-backoffice.md` §4.7). Las tareas HITL,
 * la toma de control, la KB y las skills usan los esquemas de contracts tal cual; aquí solo vive lo
 * que contracts aún no publica: listado de sesiones, traza por turno y consumo LLM del día.
 * Pendiente: moverlas a `packages/contracts/src/admin/` cuando WS-AG publique la traza.
 */

// Ya publicados por la API (`packages/contracts/src/admin`).
export {
  AgentUsageSchema,
  SkillVersionListSchema,
  TokenUsageSchema,
  type AgentUsage,
  type SkillVersion,
  type TokenUsage,
} from '@paguz/contracts';

export const AdminAgentSessionSummarySchema = z.object({
  id: AgentSessionIdSchema,
  agent: AgentRoleSchema,
  status: AgentSessionStatusSchema,
  channel: AgentChannelSchema,
  customer: z.object({ id: z.string(), fullName: z.string() }).nullable(),
  applicationId: ApplicationIdSchema.nullable(),
  /** Sesión con actividad reciente (en vivo). */
  live: z.boolean(),
  startedAt: TimestampSchema,
  lastActivityAt: TimestampSchema,
  turns: z.int().min(0),
  guardsTriggered: z.int().min(0),
  tokens: TokenUsageSchema,
  costUsd: z.string(),
  takenOverBy: z.string().nullable(),
  /** Última línea de la conversación (sin PII completa). */
  preview: z.string(),
});
export type AdminAgentSessionSummary = z.infer<typeof AdminAgentSessionSummarySchema>;
export const AdminAgentSessionPageSchema = paginatedSchema(AdminAgentSessionSummarySchema);

export const AgentSessionQuerySchema = z.object({
  agent: AgentRoleSchema.optional(),
  status: AgentSessionStatusSchema.optional(),
  live: z.enum(['true']).optional(),
  withGuards: z.enum(['true']).optional(),
  q: z.string().optional(),
  cursor: z.string().optional(),
});
export type AgentSessionQuery = z.infer<typeof AgentSessionQuerySchema>;

/** `agents.agent_llm_calls` (solo inserción). */
export const LlmCallSchema = z.object({
  id: z.string(),
  agentRole: AgentRoleSchema,
  provider: z.string(),
  model: z.string(),
  effort: LlmEffortSchema,
  inputTokens: z.int().min(0),
  outputTokens: z.int().min(0),
  cacheReadTokens: z.int().min(0),
  cacheWriteTokens: z.int().min(0),
  latencyMs: z.int().min(0),
  costUsd: z.string(),
  stopReason: z.string().nullable(),
  at: TimestampSchema,
});
export type LlmCall = z.infer<typeof LlmCallSchema>;

/** `agents.agent_tool_invocations` + decisión de la guarda previa a la herramienta. */
export const ToolTraceSchema = z.object({
  id: z.string(),
  toolName: z.string(),
  effect: z.enum(['read', 'draft', 'write-internal']),
  status: ToolInvocationStatusSchema,
  input: z.unknown(),
  output: z.unknown(),
  durationMs: z.int().min(0),
  guardOutcome: z.enum(['PASS', 'MODIFIED', 'BLOCKED']).nullable(),
  at: TimestampSchema,
});
export type ToolTrace = z.infer<typeof ToolTraceSchema>;

export const TranscriptMessageSchema = z.object({
  seq: z.int(),
  role: z.enum(['user', 'assistant', 'system_note', 'human_agent']),
  text: z.string(),
  inputMode: z.enum(['TEXT', 'VOICE', 'FILE']).nullable(),
  author: z.string().nullable(),
  at: TimestampSchema,
});
export type TranscriptMessage = z.infer<typeof TranscriptMessageSchema>;

export const TurnTraceSchema = z.object({
  /** `seq` del mensaje del usuario que abrió el turno. */
  seq: z.int(),
  startedAt: TimestampSchema,
  durationMs: z.int().min(0),
  llmCalls: z.array(LlmCallSchema),
  tools: z.array(ToolTraceSchema),
  guards: z.array(GuardEventSchema),
  /** Skills cargadas (`codigo@version`). */
  skills: z.array(z.string()),
  memory: z.object({
    read: z.array(z.string()),
    written: z.array(z.object({ key: z.string(), value: z.string(), status: z.string() })),
  }),
  checkpoint: z.string().nullable(),
});
export type TurnTrace = z.infer<typeof TurnTraceSchema>;

export const AdminAgentTraceSchema = z.object({
  session: AdminAgentSessionSummarySchema,
  traceId: z.string(),
  transcript: z.array(TranscriptMessageSchema),
  turns: z.array(TurnTraceSchema),
});
export type AdminAgentTrace = z.infer<typeof AdminAgentTraceSchema>;

/** `GET /admin/human-tasks/{id}` agrega `version` (concurrencia optimista). */
export const VersionedHumanTaskSchema = HumanTaskSchema.extend({ version: z.int() });

export const HumanMessageResultSchema = z.object({
  seq: z.int(),
  role: z.literal('human_agent'),
  text: z.string(),
});

export const CustomerMemorySchema = z.object({
  current: z.array(MemoryFactSchema),
  history: z.array(MemoryFactSchema),
  checkpoints: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      applicationId: z.string().nullable(),
      createdBy: z.string(),
      reason: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
});
export type CustomerMemory = z.infer<typeof CustomerMemorySchema>;
